import { useToast } from "@chakra-ui/react";
import {
  useIsMutating,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import {
  membersApi,
  useMemberGetQuery,
  useMemberMutate,
} from "modules/members/api";
import { useMemberAuthStore } from "modules/members/auth";
import { snakeIt } from "modules/utils";
import { PolicyConfig } from "pages/Dashboard/Access/AccessPolicies/Upsert/store";
import { z } from "zod";
import type { AppTenantFields } from "modules/apps/tenants";
import type { MFIdentity } from "state/members/member";
import { type CatalogAccess, resolveCatalogAccess } from "./catalogAccess";
import { isRequestSettling } from "./roleRequests";
import {
  indexRecipientOptions,
  isRecipientOptionsForbidden,
  mapWithConcurrency,
  RECIPIENT_OPTIONS_CONCURRENCY,
  RECIPIENT_OPTIONS_PATH,
  recipientOptionsBodies,
  recipientOptionsError,
  recipientOptionsItemKey,
  recipientOptionsMemberIds,
  tooManyToRequest,
  type MFRecipientRequestOptions,
  type RecipientOptions,
  type RecipientOptionsItem,
} from "./recipientRequestOptions";
import {
  eligibleAccountIds,
  eligibleIdentitiesPath,
  type EligibleAccounts,
  type EligibleIdentitiesQuery,
  type MFEligibleIdentities,
} from "./eligibleIdentities";
import type { MFPriorRequests, MFTeamAdoption } from "../tasks";
import type { MFSignature } from "../stepUp";

// Person who acted on a grant or request — the approver of a request or the
// reviewer of a grant. Server-provided display summary carrying a single
// pre-joined display name (distinct from MFRequestMember's firstName/lastName).
export type MFAccessActor = {
  name: string;
  email: string;
  imageUrl?: string | null;
};

// One target of a role request: a role the requester ticked, or something its
// route also gives (a side effect, under the lines in `sideEffectOf`).
export type MFRequestLine = {
  id: string;
  targetType: "entitlement" | "resource";
  entitlementId: string | null;
  resourceId: string | null;
  targetName: string;
  cause: "requested" | "side_effect";
  sideEffectOf: string[];
  viaResourceId: string | null;
  status:
    | "pending"
    | "provisioning"
    | "provisioned"
    | "failed"
    | "refused"
    | "cancelled"
    | "revoked"
    | "expired";
  /** The request took over a role held outside Ploy, on the role itself or on
   *  the group its route goes through. */
  claimed: "none" | "target" | "hop";
  /** How long the line was asked for; a side effect lasts as long as the
   *  longest of its causes. */
  requestedForSeconds: number | null;
  requestedIndefinite: boolean;
  expiresAt: string | null;
  endedAt: string | null;
  endReason: string | null;
  /** What the latest operation on the line's grant reported when it failed. */
  failure: MFRequestFailure | null;
};

export type MFRequestFailure = {
  message: string | null;
  hint: string | null;
  handoverFailure: string | null;
};

export type MFApprovalRequestLine = MFRequestLine & {
  coveredByStage: boolean;
  decidedBy: { id: string; name: string } | null;
};

export type MFActiveRequest = {
  id: string;
  member_id: string;
  status: "pending" | "approved";
  created_at: string;
  selectableResourceId: string;
};

// Compact member shape returned by the awaiting-approval rail (recipient +
// approvers). Mirrors the recipient-picker shape (firstName/lastName/email).
export type MFRequestMember = {
  id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  imageUrl?: string;
};

// One in-flight request the current member SUBMITTED — powers the My Access
// "Awaiting approval" rail. Served by GET /members/access/active-requests
// (camelCase from XWeb.Members.AccessRequestView.render_active_request).
export type MFMyActiveRequest = {
  id: string;
  /** `awaiting_signature`: a draft that goes for approval once its requester
   *  signs it with a passkey ("Needs your signature"). */
  status: "pending" | "approved" | "provisioning" | "awaiting_signature";
  createdAt: string;
  requestComments?: string;
  requestedForSeconds: number;
  requestedIndefinite: boolean;
  selectableResource:
    | (AppTenantFields & { id: string; name: string; imageUrl?: string })
    | null;
  catalogItem: { id: string; name: string; imageUrl?: string } | null;
  catalog: { id: string; name: string; icon?: string } | null;
  requestedBy: string;
  /** Last time reviewers were nudged (24h cooldown); null if never. */
  lastNudgedAt: string | null;
  /** Set only when the request was made on someone else's behalf. */
  onBehalfOf: MFRequestMember | null;
  /** Pending approvers of the current approval stage (may be empty). */
  approvers: MFRequestMember[];
  stageName: string | null;
  currentApprovalStage: number | null;
  totalApprovalStages: number | null;
  // Access-insight context, mirrored from the approver-side task view. Populated
  // by the "Expose access insights on member access endpoints" server change;
  // absent (undefined/null) until that lands or when there's no history.
  priorRequests?: MFPriorRequests | null;
  teamAdoption?: MFTeamAdoption | null;
  // Who approved the request — present once it leaves the pending stage
  // (approved/provisioning). Added by "Surface request approver on member
  // grants and requests".
  approvedBy?: MFAccessActor | null;
  /** Passkey signatures on the request. Absent before signed approvals ship;
   *  null where the view did not load them. Both mean "no badge". */
  signatures?: MFSignature[] | null;
  /** A role request's lines; null for a request on the resource itself. */
  lines?: MFRequestLine[] | null;
};

export type MFApprovalRequest = {
  id: string;
  status: "pending" | "approved" | "rejected";
  createdAt: string;
  selectableResourceId: string;
  requestComments?: string;
  requestedForSeconds: number;
  requestedIndefinite: boolean;
  resource: string;
  requestedFrom?: string;
  requester: string;
  approval: {
    body: string;
    title: string;
    decision: "approved" | "rejected" | null;
    decidedAt: null | string;
    id: string;
  };
  currentStageName?: string | null;
  /** The stage this approver decides needs a passkey: Approve opens the
   *  signing page instead of deciding inline. Deny never needs one. */
  currentStageRequiresPasskey?: boolean;
  /** Passkey signatures on the request. Absent before signed approvals ship;
   *  null where the view did not load them. Both mean "no badge". */
  signatures?: MFSignature[] | null;
  lines?: MFApprovalRequestLine[] | null;
  /** The account the access is for; null for the default account. */
  requestedIdentity?: MFIdentity | null;
};

export type MFDeprovisioningTask = {
  id: string;
  grantedAt: string;
  resourceId: string;
  resource: string;
  requester: string;
  metadata?: {
    access_review_account_id?: string;
    access_review_id?: string;
    flagged_entitlement_ids?: string[];
    entitlement_names?: string;
    action_type?: string;
  };
  task: {
    title: string;
    body: string;
    decision: "removed" | null;
    decidedAt: string;
    id: string;
  };
};

export type MFProvisioningTask = {
  id: string;
  status: "provisioning" | "cancelled" | "rejected" | "expired" | "provisioned";
  createdAt: string;
  selectableResourceId: string;
  requestComments?: string;
  requestedForSeconds: number;
  requestedIndefinite: boolean;
  resource: string;
  requestedFrom?: string;
  requester: string;
  approval: {
    body: string;
    title: string;
    decision: "approved" | "rejected" | null;
    decidedAt: null | string;
    id: string;
  };
};

export type MFAccessRequest = {
  id: string;
  status:
    | "awaiting_signature"
    | "pending"
    | "approved"
    | "cancelled"
    | "rejected"
    | "provisioning"
    | "provisioned"
    | "deprovisioning"
    | "deprovisioned"
    | "failed"
    | "expired"
    | "refused";
  createdAt: string;
  selectableResourceId: string;
  requestedForSeconds: number;
  requestedIndefinite: boolean;
  requestComments: string;
  lines?: MFRequestLine[] | null;
  events: {
    id: string;
    status:
      | "created"
      | "skipped"
      | "rejected"
      | "approved"
      | "waiting_approval"
      | "provisioning"
      | "cancelled"
      | "deprovisioning"
      | "info"
      | "provisioned"
      | "deprovisioned"
      | "failed";
    stepId: string;
    message: string;
    occuredAt: string;
  }[];
  requesterSignatureRequired?: boolean;
  /** Passkey signatures on the request. Absent before signed approvals ship;
   *  null where the view did not load them. Both mean "no badge". */
  signatures?: MFSignature[] | null;
  /** The account the access is for; null for the default account. */
  requestedIdentity?: MFIdentity | null;
};

/** What a single create returns (AccessRequestView): a draft comes back as
 *  `awaiting_signature` and needs its requester's signature. */
export type MFCreatedAccessRequest = {
  id: string;
  status: string;
  requesterSignatureRequired?: boolean;
};

export type MFAccessPolicy = {
  id: string;
  name: string;
  description: string;
  fields: any; // You might want to define a more specific type for fields
  config: PolicyConfig; // You might want to define a more specific type for config
  requiresApproval: boolean;
};

export type MFResource = AppTenantFields & {
  id: string;
  name: string;
  imageUrl?: string;
  type: string;
  intId: string;
};

export type MFSelectableResource = AppTenantFields & {
  id: string;
  type: string;
  typeId: string;
  name: string;
  description: string;
  resourceId: string;
  launchUrl?: string;
  resource?: MFResource;
  position: number;
  imageUrl: string;
  allowIndefinite: boolean;
  /** A policy allows ONLY indefinite access: a time-limited request is refused. */
  indefiniteOnly?: boolean;
  maxTimeUnit: PolicyConfig["maxDuration"]["unit"];
  maxTimeValue: PolicyConfig["maxDuration"]["value"];
  requiresApproval: boolean;
  requiresComment: boolean;
  /** The roles the member may request, on a resource whose access follows its
   *  roles: a request there names at least one. null on any other resource. */
  entitlements?: MFRequestableRole[] | null;
};

export type MFRoleTerms = {
  allowIndefinite: boolean;
  indefiniteOnly: boolean;
  maxTimeUnit: string | null;
  maxTimeValue: number | null;
  requiresComment: boolean;
  maxDurationPolicy: { id: string; name: string } | null;
};

export type MFKnobSource = {
  id: string;
  name: string;
  tier: "entitlement" | "resource" | "all";
} | null;

export type MFRoleHolding = {
  kind: "held" | "through_group" | "outside_ploy";
  groups: { id: string; name: string }[];
};

export type MFRequestableRole = {
  id: string;
  name: string;
  description: string | null;
  /** Whether any line a request for it carries, the role or what its route
   *  also gives, needs an approver. */
  requiresApproval: boolean;
  terms: MFRoleTerms;
  /** The policy each term came from, when a policy set it. */
  knobSources: { duration: MFKnobSource; requireReason: MFKnobSource };
  holding: MFRoleHolding | null;
  /** The group a request for this role joins, when it is given through one. */
  via: { id: string; name: string } | null;
  /** What else that group gives which the member doesn't already hold. */
  consequences: {
    type: "entitlement" | "resource";
    id: string;
    name: string;
  }[];
};

export type MFCatalogItem = {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  catalogItemSelectableResources: MFCatalogItemSelectableResource[];
};

export type MFCatalogItemSelectableResource = {
  name: string;
  description: string;
  imageUrl: string;
  selectableResource: MFSelectableResource;
};

export type MFAccessCatalog = {
  id: string;
  name: string;
  description: string;
  items: MFCatalogItem[];
  icon?: string;
};

export type MFSelectableResourceAccess = {
  id: string;
  member_id: string;
  selectableResourceId: string;
  catalogItemId: string;
  isIndefinite: boolean;
  expiresAt: string;
  checkExtensionAt?: string;
  grantedAt: string;
  resourceId: string;
  extendUntil?: string;
  canExtend?: boolean;
  /** A request stands behind this grant, so the member may revoke it themselves. */
  canRevoke?: boolean;
  selectableResource?: MFSelectableResource;
  status: "active" | "expired";
  /** Catalog this grant belongs to (for the "catalog › app" row breadcrumb). */
  catalogName?: string | null;
  /** Catalog-item (app) name, e.g. "Google Groups". */
  appName?: string | null;
  /** Catalog-item logo (a real image URL, unlike the SR's own image which is
   *  often a legacy icon sentinel like "FiBox"). */
  appImageUrl?: string | null;
  // Access-insight context for the My Access detail card, mirrored from the
  // approver-side task view. Populated by "Expose access insights on member
  // access endpoints"; absent until that lands or when there's no history.
  priorRequests?: MFPriorRequests | null;
  teamAdoption?: MFTeamAdoption | null;
  /** Who recertifies this grant at the next access review, when known. Added by
   *  "Surface request approver on member grants and requests". */
  reviewedBy?: MFAccessActor | null;
  /** The access request this grant originated from, when it came through the
   *  request flow. The detail drawer fetches that request to show its full
   *  lifecycle timeline; null/absent for auto-granted access (falls back to the
   *  grant-only timeline). Added by ploy-server managed-access payload. */
  originatingRequestId?: string | null;
};

const useListAccessCatalogs = (): MFAccessCatalog[] => {
  const data = useMemberGetQuery<MFAccessCatalog[]>(
    ["mf", "access", "catalogs"],
    "/members/access/catalogs",
  );
  return data.data || [];
};

// Single-catalog view of the visible-catalogs list query — shares its cache, so
// no extra request. State logic lives in `resolveCatalogAccess`.
const useGetAccessCatalog = (id?: string): CatalogAccess => {
  const query = useMemberGetQuery<MFAccessCatalog[]>(
    ["mf", "access", "catalogs"],
    "/members/access/catalogs",
  );
  return resolveCatalogAccess(query.data || [], id, query.isLoading);
};

const useListCurrentAccess = (
  catalogId: string,
): MFSelectableResourceAccess[] => {
  const data = useMemberGetQuery<MFSelectableResourceAccess[]>(
    ["mf", "access", "current_access", catalogId],
    `/members/access/catalogs/${catalogId}/current-access`,
  );
  return data.data || [];
};

// Every current-access row across ALL catalogs visible to the member — powers
// the "You have access" badges on cross-catalog surfaces (the catalog index
// page and the Request Access modal). Per-catalog pages keep
// useListCurrentAccess.
const useListAllCurrentAccess = (): MFSelectableResourceAccess[] => {
  const data = useMemberGetQuery<MFSelectableResourceAccess[]>(
    ["mf", "access", "current_access", "all"],
    "/members/access/current-access",
  );
  return data.data || [];
};

const useListActiveRequests = (catalogId: string): MFActiveRequest[] => {
  const data = useMemberGetQuery<MFActiveRequest[]>(
    ["mf", "access", "active_requests", catalogId],
    `/members/access/catalogs/${catalogId}/active-requests`,
  );
  return data.data || [];
};

// Returns `{ data, isLoading }` (not a bare array) so callers can distinguish
// "still loading" from "genuinely empty" and avoid flashing an empty state.
const useListManagedAccess = (): {
  data: MFSelectableResourceAccess[];
  isLoading: boolean;
} => {
  const query = useMemberGetQuery<MFSelectableResourceAccess[]>(
    ["mf", "access", "managed-access"],
    "/members/access/managed-access",
  );
  return { data: query.data || [], isLoading: query.isLoading };
};

// All in-flight requests the current member submitted, across every catalog —
// powers the My Access "Awaiting approval" rail. `{ data, isLoading }` shape so
// the page can show a skeleton instead of a premature empty state.
const useListMyActiveRequests = (): {
  data: MFMyActiveRequest[];
  isLoading: boolean;
} => {
  const query = useMemberGetQuery<MFMyActiveRequest[]>(
    ["mf", "access", "my_active_requests"],
    "/members/access/active-requests",
  );
  return { data: query.data || [], isLoading: query.isLoading };
};

const RoleRequestInputSchema = z.object({
  entitlementId: z.string(),
  indefiniteRequest: z.boolean(),
  requestedForUnit: z.string().optional(),
  requestedForValue: z.number().optional(),
});

export type RoleRequestInput = z.infer<typeof RoleRequestInputSchema>;

// A request on a resource whose access follows its roles carries a duration per
// role and none of its own: the server refuses a request-level duration beside
// `roles`.
export const CreateAccessRequestInputSchema = z.object({
  requestedForUnit: z.string().optional(),
  requestedForValue: z.number().optional(),
  catalogItemId: z.string(),
  indefiniteRequest: z.boolean().optional(),
  selectableResourceId: z.string(),
  comments: z.string().optional(),
  accessRequestedForMemberId: z.string(),
  identityId: z.string().optional(),
  roles: z.array(RoleRequestInputSchema).optional(),
});

export type CreateAccessRequestInput = z.infer<
  typeof CreateAccessRequestInputSchema
>;

export const useCreateAccessRequest = () => {
  const { mutate, isLoading } = useMemberMutate<MFCreatedAccessRequest>(
    "POST",
    "/members/access/catalogs/request",
    [
      ["mf", "access", "active_requests"],
      ["mf", "access", "current_access"],
      // My Access page: the awaiting-approval rail + granted-access list.
      ["mf", "access", "my_active_requests"],
      ["mf", "access", "managed-access"],
    ],
  );
  const toast = useToast();
  return {
    isLoading,
    // A server refusal is toasted by useMemberMutate; a request that never
    // reaches the server is toasted here, so every caller says something once.
    mutate: (body: CreateAccessRequestInput) => {
      const payload = CreateAccessRequestInputSchema.safeParse(body);
      if (payload.success) return mutate(snakeIt(payload.data));
      toast({
        status: "error",
        title: "Your request couldn't be sent",
        description:
          "Please try again. If the problem persists please contact support@joinploy.com",
        variant: "subtle",
      });
      return Promise.reject("Invalid data provided");
    },
  };
};

const REQUEST_POLL_MS = 3000;

const MY_ACCESS_LIST_KEYS = [
  ["mf", "access", "managed-access"],
  ["mf", "access", "my_active_requests"],
  ["mf", "access", "active_requests"],
  ["mf", "access", "current_access"],
];

// `suspense: false` lets a caller opt out of the global suspense default — used
// by the My Access detail drawer, which fetches the event log lazily after the
// drawer is already open and must NOT suspend (that would blank the whole page
// behind the route-level fallback). Full-page callers keep suspending.
// `useErrorBoundary: false` likewise keeps a failed read off the route error
// boundary, for a page that only wants a detail from it (the signing page).
// `live` polls while the request is still settling, and refreshes the My Access
// lists once it settles, since the request leaves the awaiting rail and its
// access joins the active list.
const useGetAccessRequest = (
  requestId?: string | null,
  opts: { suspense?: boolean; useErrorBoundary?: boolean; live?: boolean } = {},
) => {
  const { live = false, ...queryOpts } = opts;
  const queryClient = useQueryClient();
  const { data } = useMemberGetQuery<MFAccessRequest>(
    ["mf", "access", "request", requestId],
    `/members/access/requests/${requestId}`,
    {
      enabled: Boolean(requestId),
      ...queryOpts,
      refetchInterval: (raw?: { data?: MFAccessRequest }) =>
        live && isRequestSettling(raw?.data) ? REQUEST_POLL_MS : false,
    },
  );

  const settling = isRequestSettling(data);
  const wasSettling = useRef(settling);
  useEffect(() => {
    if (live && wasSettling.current && !settling)
      MY_ACCESS_LIST_KEYS.forEach((queryKey) =>
        queryClient.invalidateQueries({ queryKey }),
      );
    wasSettling.current = settling;
  }, [live, settling, queryClient]);

  return data;
};

// This now uses assignments under the hood not approval requests.
// `suspense` / `useErrorBoundary`: see useGetAccessRequest.
const useGetApprovalRequest = (
  requestId?: string | null,
  opts: { suspense?: boolean; useErrorBoundary?: boolean } = {},
) => {
  const { data } = useMemberGetQuery<MFApprovalRequest>(
    ["mf", "approval", "request", requestId],
    `/members/access/approval-requests/${requestId}`,
    {
      enabled: Boolean(requestId),
      ...opts,
      onError: (err) => {
        return null;
      },
    },
  );
  return data;
};

const useGetProvisioningTask = (assigneeId?: string | null) => {
  const { data } = useMemberGetQuery<MFProvisioningTask>(
    ["mf", "approval", "request", assigneeId],
    `/members/access/provisioning/${assigneeId}`,
    {
      enabled: Boolean(assigneeId),
      onError: (err) => {
        return null;
      },
    },
  );
  return data;
};

const useGetDeprovisioningTask = (assigneeId?: string | null) => {
  const { data } = useMemberGetQuery<MFDeprovisioningTask>(
    ["mf", "approval", "request", assigneeId],
    `/members/access/deprovisioning/${assigneeId}`,
    {
      enabled: Boolean(assigneeId),
      onError: (err) => {
        return null;
      },
    },
  );
  return data;
};

// Entitlement adjustment task uses the same structure as deprovisioning
const useGetEntitlementAdjustmentTask = (assigneeId?: string | null) => {
  const { data } = useMemberGetQuery<MFDeprovisioningTask>(
    ["mf", "approval", "entitlement-adjustment", assigneeId],
    `/members/access/entitlement-adjustment/${assigneeId}`,
    {
      enabled: Boolean(assigneeId),
      onError: (err) => {
        return null;
      },
    },
  );
  return data;
};

export const HandleApprovalResponseInputSchema = z.object({
  comment: z.string().optional(),
  decision: z.enum(["approve", "deny"]),
  requestId: z.string(),
  actionAssigneeId: z.string(),
  actionInstanceId: z.string(),
  requestStepId: z.string().optional(),
});

export type HandleApprovalResponseInput = z.infer<
  typeof HandleApprovalResponseInputSchema
>;

const useDecideAccessRequest = (requestId: string) => {
  const { mutate, isLoading } = useMemberMutate(
    "POST",
    "/members/access/approval-requests/" + requestId,
    [["mf", "approval", "request"]],
  );
  return {
    isLoading,
    mutate: (body: HandleApprovalResponseInput) => {
      const payload = HandleApprovalResponseInputSchema.safeParse(body);
      return payload.success
        ? mutate(snakeIt(payload.data))
        : Promise.reject(
            "Invalid data provided: " + z.prettifyError(payload.error),
          );
    },
  };
};

export const HandleProvTaskInputSchema = z.object({
  result: z.enum(["completed", "refused"]),
  resourceAccessId: z.string(),
  actionAssigneeId: z.string(),
});

export type HandleProvTaskInput = z.infer<typeof HandleProvTaskInputSchema>;
const useDecideProvisioningTask = (assigneeId: string) => {
  const { mutate, isLoading } = useMemberMutate(
    "POST",
    "/members/access/provisioning/" + assigneeId,
    [["mf", "approval", "request"]],
  );
  return {
    isLoading,
    mutate: (body: HandleProvTaskInput) => {
      const payload = HandleProvTaskInputSchema.safeParse(body);
      return payload.success
        ? mutate(snakeIt(payload.data))
        : Promise.reject(
            "Invalid data provided: " + z.prettifyError(payload.error),
          );
    },
  };
};

export const HandleDeprovTaskInputSchema = z.object({
  result: z.enum(["completed", "refused"]),
  resourceAccessId: z.string(),
  actionAssigneeId: z.string(),
});

export type HandleDeprovTaskInput = z.infer<typeof HandleDeprovTaskInputSchema>;
const useDecideDeprovisioningTask = (assigneeId: string) => {
  const { mutate, isLoading } = useMemberMutate(
    "POST",
    "/members/access/deprovisioning/" + assigneeId,
    [
      ["mf", "approval", "request"],
      ["mf", "assignments"],
    ],
  );
  return {
    isLoading,
    mutate: (body: HandleDeprovTaskInput) => {
      const payload = HandleDeprovTaskInputSchema.safeParse(body);
      return payload.success
        ? mutate(snakeIt(payload.data))
        : Promise.reject(
            "Invalid data provided: " + z.prettifyError(payload.error),
          );
    },
  };
};

// Entitlement adjustment task handling
export const HandleEntitlementAdjustmentTaskInputSchema = z.object({
  result: z.enum(["completed", "refused"]),
  accessReviewAccountId: z.string(),
  resourceAccessId: z.string(),
  actionAssigneeId: z.string(),
});

export type HandleEntitlementAdjustmentTaskInput = z.infer<typeof HandleEntitlementAdjustmentTaskInputSchema>;
const useDecideEntitlementAdjustmentTask = (assigneeId: string) => {
  const { mutate, isLoading } = useMemberMutate(
    "POST",
    "/members/access-reviews/entitlement-adjustment-task",
    [
      ["mf", "approval", "request"],
      ["mf", "assignments"],
    ],
  );
  return {
    isLoading,
    mutate: (body: HandleEntitlementAdjustmentTaskInput) => {
      const payload = HandleEntitlementAdjustmentTaskInputSchema.safeParse(body);
      return payload.success
        ? mutate(snakeIt(payload.data))
        : Promise.reject(
            "Invalid data provided: " + z.prettifyError(payload.error),
          );
    },
  };
};

const useGetExtensionRequest = (accessId?: string | null) => {
  const { data } = useMemberGetQuery<MFSelectableResourceAccess>(
    ["mf", "approval", "extend", accessId],
    `/members/access/extensions/${accessId}`,
    { enabled: Boolean(accessId) },
  );
  return data;
};

export const HandleAskExtensionResponseInput = z.object({
  result: z.enum(["extend", "expire"]),
  selectableResourceAccessId: z.string(),
  // Reason for the extension. Mandatory server-side (source "web") when the
  // resource is flagged requiresComment; omitted for expire.
  comment: z.string().optional(),
});

export type HandleAskExtensionResponseInput = z.infer<
  typeof HandleAskExtensionResponseInput
>;

const useDecideExtension = (accessId: string) => {
  const { mutate, isLoading } = useMemberMutate(
    "POST",
    "/members/access/extensions/" + accessId,
    [
      ["mf", "approval"],
      ["mf", "access"],
    ],
  );
  return {
    isLoading,
    mutate: (body: HandleAskExtensionResponseInput) => {
      const payload = HandleAskExtensionResponseInput.safeParse(body);
      return payload.success
        ? mutate(snakeIt(payload.data))
        : Promise.reject(
            "Invalid data provided: " + z.prettifyError(payload.error),
          );
    },
  };
};

const useCancelRequest = (accessId: string) => {
  const { mutate, isLoading } = useMemberMutate(
    "POST",
    `/members/access/catalogs/request/${accessId}/cancel`,
    [
      ["mf", "access", "request", accessId],
      ["mf", "access", "active_requests"],
      ["mf", "access", "my_active_requests"],
    ],
  );
  return {
    isLoading,
    mutate: () => mutate({}),
  };
};

export type RevokeAccessStatus = "deprovisioned" | "deprovisioning" | "held";

// Keyed by grant so the row and the drawer, each with its own mutation, both
// see a removal the other started (`isLoading`). react-query re-keys a running
// mutation when its observer's accessId changes, so a caller whose accessId can
// change must hold it still while `isSubmitting`.
const useRevokeAccess = (accessId: string) => {
  const mutationKey = ["mf", "access", "revoke", accessId];
  const { mutate, isLoading: isSubmitting } = useMemberMutate<{ status: RevokeAccessStatus }>(
    "POST",
    `/members/access/managed-access/${accessId}/revoke`,
    [["mf", "access"]],
    { mutationKey },
  );
  return {
    isLoading: useIsMutating({ mutationKey, exact: true }) > 0,
    isSubmitting,
    mutate: async (): Promise<RevokeAccessStatus> => {
      const res = await mutate({});
      if (!res.success) throw res;
      return res.data.status;
    },
  };
};

const useNudgeReviewers = (requestId: string) => {
  const { mutate, isLoading } = useMemberMutate(
    "POST",
    `/members/access/requests/${requestId}/nudge`,
    [["mf", "access", "my_active_requests"]],
  );
  return {
    isLoading,
    mutate: () => mutate({}),
  };
};

export type EligibleRecipient = {
  id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  imageUrl?: string;
  jobTitle?: string;
  department?: string;
};

export type EligibleRecipientGroup = {
  id: string;
  name: string;
  source: "microsoft" | "google" | "okta" | string;
  /** Member ids belonging to this group that the current user is permitted
   *  to request access on behalf of (NOT the full group membership). */
  memberIds: string[];
};

export type EligibleRecipients = {
  people: EligibleRecipient[];
  groups: EligibleRecipientGroup[];
};

// All recipients the current user can request access on behalf of —
// individual people + IdP-synced groups. Powers the recipient picker in the
// bulk Request Access modal. Group entries' `memberIds` are pre-intersected
// with the user's allowed-requestor set, so expanding a group at submit
// time never produces unauthorized rows.
const useListEligibleRecipients = (): EligibleRecipients => {
  const { data } = useMemberGetQuery<EligibleRecipients>(
    ["mf", "access", "recipients"],
    "/members/access/recipients",
  );
  return data ?? { people: [], groups: [] };
};

export type RecipientRequestOptionsState = {
  /** null when nobody but the viewer is asked about: read the catalog. */
  options: RecipientOptions | null;
  loading: boolean;
  error: string | null;
  /** Ask again after a failed read; null when asking can't help. */
  retry: (() => void) | null;
};

// What each of `memberIds` may request of `items`, judged for them rather than
// for the viewer. A failure stays with the basket that asked (it says why and
// holds the send) instead of taking the route down. A refusal over someone the
// viewer may no longer request for re-reads who they may, which drops that
// person from the request and so asks again without them.
const useRecipientRequestOptions = (
  memberIds: string[],
  items: RecipientOptionsItem[],
  selfMemberId: string | undefined,
): RecipientRequestOptionsState => {
  const accessToken = useMemberAuthStore((state) => state.accessToken);
  const queryClient = useQueryClient();
  const tooMany = tooManyToRequest(memberIds, items);
  const askedIds = recipientOptionsMemberIds(memberIds, selfMemberId);
  const bodies = tooMany ? [] : recipientOptionsBodies(askedIds, items);
  const asked = bodies.length > 0;
  const query = useQuery(
    [
      "mf",
      "access",
      "recipient-request-options",
      [...askedIds].sort(),
      items.map(recipientOptionsItemKey).sort(),
      accessToken,
    ],
    async () => {
      const responses = await mapWithConcurrency(
        bodies,
        RECIPIENT_OPTIONS_CONCURRENCY,
        (body) =>
          membersApi.post<MFRecipientRequestOptions[]>(
            RECIPIENT_OPTIONS_PATH,
            body,
            accessToken as string,
          ),
      );
      return indexRecipientOptions(
        responses.map((r) => (r.success ? r.data : [])),
      );
    },
    {
      enabled: Boolean(accessToken) && asked,
      suspense: false,
      useErrorBoundary: false,
      retry: false,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      onError: (error) => {
        if (isRecipientOptionsForbidden(error))
          queryClient.invalidateQueries(["mf", "access", "recipients"]);
      },
    },
  );
  if (tooMany)
    return { options: null, loading: false, error: tooMany, retry: null };
  if (!asked) return { options: null, loading: false, error: null, retry: null };
  return {
    options: query.data ?? null,
    loading: query.isInitialLoading || query.isPreviousData,
    error: query.isError ? recipientOptionsError(query.error) : null,
    retry: query.isError ? () => void query.refetch() : null,
  };
};

export type EligibleIdentitiesState = {
  eligible: EligibleAccounts;
  loading: boolean;
};

// Which of a member's accounts the policies admit for each queried row, given
// the roles ticked on it. A failed read leaves its row unrestricted: the
// request gate still holds the line, so the basket goes on as before.
const useEligibleIdentities = (
  queries: EligibleIdentitiesQuery[],
): EligibleIdentitiesState => {
  const accessToken = useMemberAuthStore((state) => state.accessToken);
  const results = useQueries({
    queries: queries.map((q) => {
      const path = eligibleIdentitiesPath(q);
      return {
        queryKey: ["mf", "access", "eligible-identities", path, accessToken],
        queryFn: async () => {
          const res = await membersApi.get<MFEligibleIdentities>(
            path,
            accessToken as string,
          );
          return { itemId: q.itemId, answer: res.success ? res.data : undefined };
        },
        enabled: Boolean(accessToken),
        suspense: false,
        useErrorBoundary: false,
        retry: false,
        // useQueries hands a changed query the data of whichever query left,
        // which is only this row's when its own roles changed; `itemId` says.
        keepPreviousData: true,
        refetchOnWindowFocus: false,
      };
    }),
  });

  const entries = results.map((result, i) => {
    const data = result.data;
    const ids =
      data?.itemId === queries[i].itemId
        ? eligibleAccountIds(data.answer)
        : undefined;
    return [queries[i].itemId, ids] as const;
  });
  const loading = results.some((r) => r.isInitialLoading || r.isPreviousData);
  const signature = entries
    .map(([itemId, ids]) => `${itemId}:${ids ? ids.join(",") : "-"}`)
    .join("|");

  const eligible = useMemo(() => {
    const out: EligibleAccounts = {};
    for (const [itemId, ids] of entries) if (ids) out[itemId] = ids;
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
  return { eligible, loading };
};

export type BulkAccessRequestResult = {
  ok: boolean;
  error?: string;
  requestId?: string;
  /** The created request's status; `awaiting_signature` needs signing. */
  status?: string;
  selectableResourceId?: string;
  accessRequestedForMemberId?: string;
};

export type BulkAccessRequestResponse = {
  results: BulkAccessRequestResult[];
  succeeded: number;
  total: number;
};

const BulkAccessRequestInputSchema = z.object({
  requests: z.array(CreateAccessRequestInputSchema),
});
export type BulkAccessRequestInput = z.infer<typeof BulkAccessRequestInputSchema>;

// Bulk submission. Calls a single backend endpoint that loops through each
// payload using the same workflow as the per-request POST — so behaviour is
// identical to firing N individual requests, just in one HTTP roundtrip.
//
// `useMemberMutate` resolves with the `{success, data}` envelope; we unwrap
// `.data` so callers see the actual `{results, succeeded, total}` payload.
export const useBulkCreateAccessRequests = () => {
  const { mutate, isLoading } = useMemberMutate<BulkAccessRequestResponse>(
    "POST",
    "/members/access/catalogs/requests/bulk",
    [
      ["mf", "access", "active_requests"],
      ["mf", "access", "current_access"],
      // My Access page: the awaiting-approval rail + granted-access list.
      ["mf", "access", "my_active_requests"],
      ["mf", "access", "managed-access"],
    ],
  );
  return {
    isLoading,
    mutate: async (
      body: BulkAccessRequestInput,
    ): Promise<BulkAccessRequestResponse> => {
      const payload = BulkAccessRequestInputSchema.safeParse(body);
      if (!payload.success) {
        return Promise.reject("Invalid data provided");
      }
      const envelope = (await mutate(snakeIt(payload.data))) as unknown as {
        success: boolean;
        data: BulkAccessRequestResponse;
      };
      return envelope.data;
    },
  };
};

export const MF_ACCESS = {
  useListAccessCatalogs,
  useGetAccessCatalog,
  useListCurrentAccess,
  useListAllCurrentAccess,
  useListActiveRequests,
  useCreateAccessRequest,
  useGetAccessRequest,
  useCancelRequest,
  useRevokeAccess,
  useNudgeReviewers,
  useGetApprovalRequest,
  useGetProvisioningTask,
  useDecideAccessRequest,
  useGetExtensionRequest,
  useDecideProvisioningTask,
  useDecideDeprovisioningTask,
  useDecideEntitlementAdjustmentTask,
  useDecideExtension,
  useGetDeprovisioningTask,
  useGetEntitlementAdjustmentTask,
  useListEligibleRecipients,
  useRecipientRequestOptions,
  useEligibleIdentities,
  useBulkCreateAccessRequests,
  useListManagedAccess,
  useListMyActiveRequests,
};
